import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import { FiMoreVertical } from "react-icons/fi";
import { format } from "date-fns";
import { MenuItem, Grid } from "@mui/material";

import { formatGMT } from "../../../../../../util/Format";
import { formatInTimeZone } from "../../../../../../util/formatInTimezone";
import { JobStatus, JobSubStatus } from "../../../../../../util/enums";

import BadgeSubStatus from "../../../../../../components/Generic/BadgeSubStatus";
import { StyledMenu } from "../../../../../../components/Generic/AnchorMenu";

import "./index.scss";

import { differenceBetweenDates, datesAreSame } from "../../../../../../helpers/helpers";

import { Quote } from "../../../../../../models/quote";
import { Task } from "../../../../../../models/task";

import useStyles from "./styles";

interface Props {
  quote?: Quote;
  task: Task;
  actions: any;
}

function QuoteBox({ quote, task, actions }: Props) {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const getAmPmHour = (time: string) => {
    let [hour, minute] = time.split(":");
    if (Number(hour) > 12) {
      hour = (Number(hour) - 12).toString();
      return `${Number(hour) > 9 ? hour : "0" + Number(hour)}:${minute}pm`;
    }
    return `${Number(hour) > 9 ? hour : "0" + Number(hour)}:${minute}am`;
  };

  const getSpanOrigin = () => {
    if (quote && quote?.origins && quote?.origins?.length > 0 && quote?.origins[0]?.longAddress) {
      return <span className={classes.boxSubtitle}>Origin </span>;
    }
    return null;
  };

  const getSpanDestination = () => {
    if (
      quote &&
      quote?.destinations &&
      quote?.destinations?.length > 0 &&
      quote?.destinations[0]?.longAddress
    ) {
      return <span className={classes.boxSubtitle}>Destination &nbsp;</span>;
    }
    return null;
  };

  const getOrigin = () => {
    if (quote && quote?.origins && quote?.origins?.length > 0 && quote?.origins[0]?.longAddress) {
      return (
        <span className={classnames("mb-2", classes.boxText)}>
          {quote?.origins[0]?.longAddress}
        </span>
      );
    }
    return null;
  };

  const getDestination = () => {
    if (
      quote &&
      quote?.destinations &&
      quote?.destinations?.length > 0 &&
      quote?.destinations[0]?.longAddress
    ) {
      return (
        <span className={classnames("mb-2", classes.boxText)}>
          {quote?.destinations[0]?.longAddress}
        </span>
      );
    }
    return null;
  };

  const isTaskAvailable = quote && quote.tasks && quote.tasks.length > 0;

  const renderTasks = (tasks: Task[]) => {
    if (tasks && tasks.length > 0) {
      return tasks.map((task) => {
        if (quote && quote?.customer && task.startDate) {
          const gmt =
            quote && quote.customer.timezone ? formatGMT(quote.customer.timezone.gmtOffset) : null;
          const customerDate =
            gmt && quote.customer.timezone
              ? formatInTimeZone("hh:mm aaa", quote.customer.timezone.name, task.startDate)
              : null;

          return (
            <div className="d-flex flex-column">
              <Grid container direction="column">
                <Grid item xs={12}>
                  <span className={classes.boxTitle}>{task.type}</span>
                </Grid>

                <Grid xs={12} item container direction="row">
                  <Grid item xs={6}>
                    <span className={classes.taskText}>
                      {format(new Date(task.startDate), "hh:mm aaa")}
                    </span>
                  </Grid>
                  <Grid item xs={6}>
                    <span className={classes.taskText}>{translate("local")}</span>
                  </Grid>
                </Grid>

                {gmt && customerDate && (
                  <Grid xs={12} item container direction="row">
                    <Grid item xs={6}>
                      <span className={classes.taskText}>{customerDate}</span>
                    </Grid>
                    <Grid item xs={6}>
                      <span className={classes.taskText}>
                        GMT {`${gmt} ${quote?.customer?.timezone?.name.split("/")[1]}`}
                      </span>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </div>
          );
        }
      });
    }
    return null;
  };

  const diffDates = differenceBetweenDates(task.startDate, task.finishDate);

  const showBadgeSubStatus = (value?: string) => {
    return <BadgeSubStatus value={value} />;
  };

  const renderApproveDeclineButton =
    quote?.subStatus &&
    quote?.subStatus?.toLowerCase() !== JobSubStatus.WON?.toLowerCase() &&
    quote?.subStatus?.toLowerCase() !== JobSubStatus.LOST?.toLowerCase();

  const extraOptions = () => {
    if (quote?.status === JobStatus.QUOTE && quote?.subStatus !== JobSubStatus.LOST) {
      const open: boolean = !!anchorEl;

      return (
        <div>
          <div onClick={(e) => setAnchorEl(e.currentTarget)} className={classes.circleIcon}>
            <FiMoreVertical color="#93A9BD" />
          </div>
          <StyledMenu
            id="extra-actions-menu"
            anchorEl={anchorEl}
            keepMounted
            open={open}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem onClick={() => actions.handleEdit && actions.handleEdit(quote?.id)}>
              <span className={classes.taskText}>{translate("edit")}</span>
            </MenuItem>
            <MenuItem
              disabled={!datesAreSame(new Date(), new Date(task.startDate))}
              onClick={() =>
                actions.handleStartVideoSurvey && actions.handleStartVideoSurvey(quote.tasks)
              }
            >
              <span className={classes.taskText}>{translate("videoSurvey")}</span>
            </MenuItem>

            {renderApproveDeclineButton && (
              <MenuItem onClick={() => actions.handleApprove(quote?.id)}>
                <span className={classes.taskText}>{translate("approve")}</span>
              </MenuItem>
            )}

            {renderApproveDeclineButton && (
              <MenuItem onClick={() => actions.handleDecline(quote?.id)}>
                <span className={classes.taskText}>{translate("decline")}</span>
              </MenuItem>
            )}

            <MenuItem
              onClick={() => actions.handleCommunication && actions.handleCommunication(quote?.id)}
            >
              <span className={classes.taskText}>{translate("communication")}</span>
            </MenuItem>
          </StyledMenu>
        </div>
      );
    }
    return null;
  };

  return (
    <Grid
      container
      direction="row"
      spacing={2}
      justifyContent="space-between"
      className={classes.mainDiv}
    >
      <Grid item xs={5}>
        <div className="d-flex flex-row">
          <div className="d-flex flex-column mr-2 font-weight-bold">
            <div className="d-flex flex-row">
              <span className={classnames("mb-2", classes.boxTitle)}>
                {quote && `#${quote.reference}`}
              </span>
              <span className={classnames("mb-2 ml-2", classes.boxText)}>
                {quote && quote.customer?.name}
              </span>
            </div>
            <div className="d-flex flex-column">
              <div className="d-flex flex-column font-weight-bold">
                {getSpanOrigin()}
                {getOrigin()}
              </div>
              <div className="d-flex flex-column">
                {getSpanDestination()}
                {getDestination()}
              </div>
            </div>
          </div>
        </div>
      </Grid>

      <Grid item xs={4}>
        {isTaskAvailable && renderTasks(quote.tasks)}
      </Grid>

      <Grid item xs={3} container justifyContent="flex-end" alignItems="flex-start">
        <div className="font-weight-bold mr-2">
          <span>{quote && showBadgeSubStatus(quote.subStatus)}</span>
        </div>

        <div className="d-flex align-items-center">{extraOptions()}</div>
      </Grid>
    </Grid>
  );
}

export default QuoteBox;
